import { useCallback, useRef } from 'react';

import {
  LeadSmsStateDocument,
  LeadSmsStateFieldsFragment,
  LeadSmsStateQuery,
  LeadSmsStateQueryVariables,
  useLeadSmsPreviewQuery,
  useLeadSmsStateQuery,
  useWriteLeadSmsMutation,
  WriteLeadSmsMutationVariables,
} from 'core/graphql';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { ISmsError, smsCrashErrors, toSmsErrors } from '../Sms/smsFeedback';
import {
  ISmsLinkDraft,
  isSmsInFlight,
  SMS_STATE_POLL_MS,
  smsLinkInput,
} from '../Sms/smsModel';
import { useSmsPreviewInput } from '../Sms/useSmsPreviewInput';
import type {
  IStudentSmsSendInput,
  IStudentSmsSendResult,
  IUseSmsPreview,
} from '../Sms/useStudentSms';
import { useLeadPermissions } from './useLeadPermissions';

/**
 * ТЗ 0084 — SMS лиду из его карточки: зеркало `useTeacherSms`. Можно ли и
 * куда уйдёт, история его SMS, живой предпросмотр и отправка. Писать может
 * любой продавец любому лиду (`LEAD_ROLES`).
 */

export type ILeadSmsState = LeadSmsStateFieldsFragment;

export const useLeadSmsState = (leadId: string, enabled = true) => {
  const { canUse } = useLeadPermissions();
  const skip = !canUse || !enabled || !leadId;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useLeadSmsStateQuery({
    variables: { leadId },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const state = data?.kommoLeadSms ?? null;
  // Опрос — только пока что-то в пути (как у студента и преподавателя).
  const inFlight = Boolean(state && isSmsInFlight(state.messages));
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    SMS_STATE_POLL_MS,
    !skip && inFlight,
  );
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    canUse,
    state,
    /** Какая-то SMS ещё в пути — исход увидит только опрос. */
    inFlight,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
  };
};

/**
 * Живой предпросмотр. `network-only` и прошлый ответ до нового — по той же
 * причине, что у студента (`useStudentSmsPreview`).
 */
export const useLeadSmsPreview = (
  leadId: string,
  text: string,
  link: ISmsLinkDraft,
  enabled = true,
): IUseSmsPreview => {
  const { canUse } = useLeadPermissions();
  const { debounced, lagging, hasText } = useSmsPreviewInput(text, link);
  const skip = !canUse || !enabled || !leadId || !debounced.text;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useLeadSmsPreviewQuery({
    variables: { leadId, text: debounced.text, link: debounced.link },
    skip,
    fetchPolicy: 'network-only',
  });
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  if (!hasText) {
    return { preview: null, pending: false, failed: false, reload };
  }
  const current = (data ?? previousData)?.previewLeadSms ?? null;
  return {
    preview: current,
    pending: lagging || loading,
    failed: Boolean(error) && !current,
    reload,
  };
};

export type ILeadSmsSendInput = IStudentSmsSendInput;
export type ILeadSmsSendResult = IStudentSmsSendResult;

/**
 * Отправить SMS лиду. Ответ несёт состояние целиком — оно пишется в запрос
 * карточки (не `refetchQueries` по имени — грабли
 * `refetch-by-name-skipped-query`).
 */
export const useLeadSmsSend = (leadId: string) => {
  const [sendMut, { loading: sending }] = useWriteLeadSmsMutation();
  const inFlight = useRef(false);

  const send = useCallback(
    async (input: ILeadSmsSendInput): Promise<ILeadSmsSendResult> => {
      if (inFlight.current) return { ok: false, errors: [], status: null };
      inFlight.current = true;
      try {
        const variables: WriteLeadSmsMutationVariables = {
          leadId,
          text: input.text.trim(),
          link: smsLinkInput(input.link),
          templateId: input.templateId || null,
          requestKey: input.requestKey,
        };
        const { data } = await sendMut({
          variables,
          update: (cache, { data: answer }) => {
            const state = answer?.sendLeadSms?.state;
            if (!state) return;
            cache.writeQuery<LeadSmsStateQuery, LeadSmsStateQueryVariables>({
              query: LeadSmsStateDocument,
              variables: { leadId },
              data: { kommoLeadSms: state },
            });
          },
        });
        const payload = data?.sendLeadSms;
        const errors: ISmsError[] = toSmsErrors(payload?.customErrors);
        return {
          ok: Boolean(payload?.success) && !errors.length,
          errors,
          status: payload?.message?.status ?? null,
          body: payload?.message?.body ?? null,
        };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught), status: null };
      } finally {
        inFlight.current = false;
      }
    },
    [leadId, sendMut],
  );

  return { send, sending };
};
